// ===============================================
// 🔒 ADMIN API SERVICE - MANEJO DE TOKENS
// ===============================================
// Archivo: src/services/adminApiService.js
import api from '../config/apiConfig';
const API_BASE = import.meta.env.VITE_API_URL || 'https://app.ingeai7.com/api';

export const uploadDocument = async (
  file: File,
  metadata: {
    title: string;
    description?: string;
    category: string;
    tags?: string[];
  }
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', metadata.title);
  if (metadata.description) formData.append('description', metadata.description);
  formData.append('category', metadata.category);
  if (metadata.tags?.length) formData.append('tags', JSON.stringify(metadata.tags));

  const response = await api.post('/api/admin/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// ===============================================
// 🔒 FUNCIÓN BASE CON AUTENTICACIÓN
// ===============================================
/**
 * 🔒 Verificar si el token ha expirado ANTES de hacer request 
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    
    // Verificar si el token expirará en los próximos 30 segundos
    return payload.exp <= (now + 30);
  } catch {
    return true;
  }
};
/**
 * 🔑 Realizar request con verificación proactiva de token
 */
const apiRequest = async (endpoint, options = {}) => {
  console.log('🔍 [API-REQUEST] Iniciando request a:', endpoint);
  
  // POR esta línea que prioriza kikai_token:
    const token = localStorage.getItem('kikai_token') || 
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');

// Y cambiar la verificación:
if (!token) {
  console.error('❌ [API-REQUEST] No kikai_token found, redirecting to login');
  window.location.href = '/login';
  throw new Error('No authentication token');
}

  console.log('✅ [TOKEN-SELECTED] Usando token:', token.substring(0, 30) + '...');

  // ✅ DECODIFICAR TOKEN PARA VER QUE CONTIENE
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    console.log('🔍 [TOKEN-PAYLOAD] Contenido del token:', {
      id: payload.id,
      userId: payload.userId,
      email: payload.email,
      role: payload.role,
      exp: payload.exp,
      iat: payload.iat,
      hasRole: payload.role !== undefined,
      isExpired: payload.exp < (Date.now() / 1000)
    });
    
    // ✅ VERIFICAR EXPIRACIÓN
    if (payload.exp < (Date.now() / 1000)) {
      console.error('❌ [TOKEN-EXPIRED] Token expirado, removiendo');
      localStorage.removeItem('kikai_token');
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Token expired');
    }
  } catch (e) {
    console.error('❌ [TOKEN-DECODE] Error decodificando token:', e);
  }

  const url = endpoint.startsWith('http') ? 
    endpoint : 
    `${API_BASE}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    },
    ...options
  };

  console.log('🔗 [API-REQUEST] Request details:', {
    method: options.method || 'GET',
    url: url,
    hasAuthHeader: !!defaultOptions.headers['Authorization'],
    authHeaderPreview: defaultOptions.headers['Authorization'].substring(0, 30) + '...'
  });

  try {
    const response = await fetch(url, defaultOptions);
    
    console.log('📡 [API-RESPONSE] Response status:', response.status);
    
    if (response.status === 401) {
      console.error('❌ [API-REQUEST] 401 - Token rejected by server');
      
      // Intentar obtener detalles del error
      try {
        const errorData = await response.clone().json();
        console.error('❌ [API-ERROR] Server error details:', errorData);
      } catch (e) {
        console.error('❌ [API-ERROR] Could not parse error response');
      }
      
      localStorage.removeItem('kikai_token');
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Token expired or invalid');
    }
    
    if (response.status === 403) {
      console.error('❌ [API-REQUEST] 403 - Admin access denied');
      throw new Error('Admin access required');
    }
    
    console.log('✅ [API-REQUEST] Request successful');
    return response;
    
  } catch (error) {
    console.error(`❌ [API-REQUEST] Error calling ${url}:`, error.message);
    throw error;
  }
};

// ===============================================
// 👥 FUNCIONES DE USUARIOS
// ===============================================

export const adminUserService = {
  // GET usuarios con paginación y filtros
  async getUsers(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await apiRequest(`/admin/users?${searchParams}`);
    
    if (!response.ok) {
      throw new Error(`Error loading users: ${response.status}`);
    }
    
    return response.json();
  },

  // POST crear usuario
  async createUser(userData) {
    const response = await apiRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error creating user');
    }
    
    return response.json();
  },

  // PUT actualizar usuario
  async updateUser(userId, userData) {
    const response = await apiRequest(`/admin/users/${userId}`, {
      method: 'PUT', 
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error updating user');
    }
    
    return response.json();
  },

  // DELETE eliminar usuario
  async deleteUser(userId) {
    const response = await apiRequest(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error deleting user');
    }
    
    return response.json();
  },

  // POST eliminación múltiple
  async bulkDeleteUsers(userIds) {
    const response = await apiRequest('/admin/users/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids: userIds })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error in bulk delete');
    }
    
    return response.json();
  }
};

// ===============================================
// 👑 FUNCIONES DE PLANES
// ===============================================

export const adminPlanService = {
  async getPlans() {
    const response = await apiRequest('/admin/plans');
    
    if (!response.ok) {
      throw new Error(`Error loading plans: ${response.status}`);
    }
    
    return response.json();
  },

  async createPlan(planData) {
    const response = await apiRequest('/admin/plans', {
      method: 'POST',
      body: JSON.stringify(planData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error creating plan');
    }
    
    return response.json();
  },

  async updatePlan(planId, planData) {
    const response = await apiRequest(`/admin/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error updating plan');
    }
    
    return response.json();
  },

  async deletePlan(planId) {
    const response = await apiRequest(`/admin/plans/${planId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error deleting plan');
    }
    
    return response.json();
  }
};

// ===============================================
// 💳 FUNCIONES DE PAGOS
// ===============================================

export const adminPaymentService = {
  async getPayments(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await apiRequest(`/admin/payments?${searchParams}`);
    
    if (!response.ok) {
      throw new Error(`Error loading payments: ${response.status}`);
    }
    
    return response.json();
  },

  async refundPayment(paymentId, reason = '') {
    const response = await apiRequest(`/admin/payments/${paymentId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error processing refund');
    }
    
    return response.json();
  }
};

// ===============================================
// 📄 FUNCIONES DE DOCUMENTOS
// ===============================================

export const adminDocumentService = {
  async getDocuments(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await apiRequest(`/admin/documents?${searchParams}`);
    
    if (!response.ok) {
      throw new Error(`Error loading documents: ${response.status}`);
    }
    
    return response.json();
  },

  async uploadDocument(file, metadata) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await apiRequest('/admin/documents/upload', {
      method: 'POST',
      body: formData,
      headers: {} // No Content-Type header for FormData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error uploading document');
    }
    
    return response.json();
  },

  async deleteDocument(documentId) {
    const response = await apiRequest(`/admin/documents/${documentId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error deleting document');
    }
    
    return response.json();
  }
};

// ===============================================
// ❓ FUNCIONES DE FAQ
// ===============================================

export const adminFaqService = {
  async getFaqs() {
    const response = await apiRequest('/admin/faqs');
    
    if (!response.ok) {
      throw new Error(`Error loading FAQs: ${response.status}`);
    }
    
    return response.json();
  },

  async createFaq(faqData) {
    const response = await apiRequest('/admin/faqs', {
      method: 'POST',
      body: JSON.stringify(faqData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error creating FAQ');
    }
    
    return response.json();
  },

  async updateFaq(faqId, faqData) {
    const response = await apiRequest(`/admin/faqs/${faqId}`, {
      method: 'PUT',
      body: JSON.stringify(faqData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error updating FAQ');
    }
    
    return response.json();
  },

  async deleteFaq(faqId) {
    const response = await apiRequest(`/admin/faqs/${faqId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error deleting FAQ');
    }
    
    return response.json();
  }
};

// ===============================================
// 📊 FUNCIONES DE ESTADÍSTICAS Y ANALYTICS
// ===============================================

export const adminStatsService = {
  async getSystemStats() {
    const response = await apiRequest('/admin/stats');
    if (!response.ok) {
      throw new Error(`Error loading system stats: ${response.status}`);
    }
    return response.json();
  },

  async getSearchStats() {
    const response = await apiRequest('/analytics/search-stats');
    
    if (!response.ok) {
      throw new Error(`Error loading search stats: ${response.status}`);
    }
    
    return response.json();
  },

  async getPopularQueries() {
    const response = await apiRequest('/analytics/popular');
    
    if (!response.ok) {
      throw new Error(`Error loading popular queries: ${response.status}`);
    }
    
    return response.json();
  },

  async getEquipment() {
    const response = await apiRequest('/admin/equipment');
    
    if (!response.ok) {
      throw new Error(`Error loading equipment: ${response.status}`);
    }
    
    return response.json();
  }
};

// ===============================================
// 📤 FUNCIONES DE EXPORTACIÓN
// ===============================================

export const adminExportService = {
  async exportUsers(format = 'csv', filters = {}) {
    const response = await apiRequest('/admin/export/users', {
      method: 'POST',
      body: JSON.stringify({ format, filters })
    });
    
    if (!response.ok) {
      throw new Error(`Error exporting users: ${response.status}`);
    }
    
    // Para downloads, retornar blob
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `users_export_${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Export downloaded successfully' };
  },

  async exportPayments(format = 'csv', filters = {}) {
    const response = await apiRequest('/admin/export/payments', {
      method: 'POST',
      body: JSON.stringify({ format, filters })
    });
    
    if (!response.ok) {
      throw new Error(`Error exporting payments: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `payments_export_${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Export downloaded successfully' };
  }
};

resetPassword: async (userId: string, password: string) => {
  const response = await fetch(`${API_URL}/admin/users/${userId}/reset-password`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getToken()}`
    },
    body: JSON.stringify({ password })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Error resetting password');
  }

  return response.json();
}

// ===============================================
// 🔧 FUNCIONES DE UTILIDAD
// ===============================================

export const adminUtilService = {
  // Verificar si el usuario actual es admin
  isAdmin() {
    const token = localStorage.getItem('kikai_token') || 
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === 'admin';
    } catch {
      return false;
    }
  },

  // Obtener información del usuario actual
  getCurrentUser() {
    const token = localStorage.getItem('kikai_token') || 
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.userId,
        email: payload.email,
        role: payload.role,
        exp: payload.exp
      };
    } catch {
      return null;
    }
  },

  // ✅ VERIFICAR EXPIRACIÓN MEJORADA
  isTokenExpired() {
    const token = localStorage.getItem('kikai_token') || 
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    
    return isTokenExpired(token);
  },

  // ✅ NUEVA: Limpiar todos los tokens
  clearAllTokens() {
    localStorage.removeItem('token');
    localStorage.removeItem('kikai_token');
    sessionStorage.removeItem('token');
  }
};


console.log('🔒 [ADMIN-API-SERVICE] Servicio configurado con:');
console.log('   📊 Endpoints corregidos para coincidir con backend');
console.log('   🔒 Verificación proactiva de tokens');
console.log('   🔄 Datos de fallback para funcionalidad básica');
console.log('✅ [ADMIN-API-SERVICE] Listo para usar');